import { useEffect, useState, type ReactNode } from 'react';
import { Clock3, RefreshCw, ShieldCheck } from 'lucide-react';
import {
  announcedBuildTarget,
  buildFingerprint,
  createWebBuildAnnouncement,
  resolveWebBuildTarget,
  WebBuildUpdateController,
  WEB_BUILD_UPDATE_STORAGE_KEY,
  type WebBuildUpdateSnapshot,
} from '../../WebBuildUpdate';
import { sipManager } from '../SipManager';

const BUILD_CHECK_INTERVAL_MS = 60_000;
const BUILD_CHECK_TIMEOUT_MS = 5_000;
const BUILD_CHECK_RETRY_MS = 3_000;

function loadedBuildId(): string {
  return buildFingerprint(document.head.innerHTML) || '';
}

function sharedBuildAnnouncement(): string | null {
  try {
    return localStorage.getItem(WEB_BUILD_UPDATE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function WebBuildUpdateGuard({ children }: { children: ReactNode }) {
  const [currentBuildId] = useState(loadedBuildId);
  const [ready, setReady] = useState(false);
  const [bootstrapError, setBootstrapError] = useState('');
  const [controller] = useState(() => new WebBuildUpdateController({
    phoneProtected: () => sipManager.hasLiveCallActivity(),
    reload: () => window.location.reload(),
  }));
  const [update, setUpdate] = useState<WebBuildUpdateSnapshot>(() => controller.getSnapshot());

  useEffect(() => controller.subscribe(setUpdate), [controller]);

  useEffect(() => {
    const reconcile = () => { void controller.phoneStateChanged(); };
    const unsubscribeSnapshot = sipManager.on('snapshot', reconcile);
    const unsubscribeActivity = sipManager.on('callActivity', reconcile);
    return () => {
      unsubscribeSnapshot();
      unsubscribeActivity();
    };
  }, [controller]);

  useEffect(() => {
    if (!currentBuildId) {
      setBootstrapError('Не удалось определить версию загруженного портала. Обновите страницу.');
      return;
    }

    let disposed = false;
    let initialized = false;
    let initializing = false;
    let checking = false;
    let timer: number | null = null;
    let initializationRetry: number | null = null;

    const publishServerTarget = (targetBuildId: string) => {
      try {
        if (announcedBuildTarget(sharedBuildAnnouncement()) === targetBuildId) return;
        localStorage.setItem(
          WEB_BUILD_UPDATE_STORAGE_KEY,
          createWebBuildAnnouncement(targetBuildId),
        );
      } catch {
        // Остальные вкладки всё равно обнаружат сборку собственным запросом.
      }
    };

    const reconcileTarget = async (targetBuildId: string | null) => {
      if (disposed || !targetBuildId) return;
      if (targetBuildId === currentBuildId) {
        if (sipManager.confirmWebBuildCurrent(targetBuildId)) {
          await controller.reportBuildCurrent();
        }
        return;
      }

      sipManager.markWebBuildStale(targetBuildId);
      await controller.reportBuildChanged();
    };

    const requestServerIndex = async (): Promise<string | null> => {
      const abortController = new AbortController();
      const timeout = window.setTimeout(
        () => abortController.abort(),
        BUILD_CHECK_TIMEOUT_MS,
      );
      try {
        const response = await fetch(`/index.html?build-check=${Date.now()}`, {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: abortController.signal,
        });
        return response.ok ? await response.text() : null;
      } catch {
        return null;
      } finally {
        window.clearTimeout(timeout);
      }
    };

    const check = async () => {
      if (disposed || checking || !initialized) return;
      checking = true;
      try {
        const html = await requestServerIndex();
        const targetBuildId = html ? buildFingerprint(html) : null;
        if (!targetBuildId || disposed) return;
        publishServerTarget(targetBuildId);
        await reconcileTarget(targetBuildId);
      } finally {
        checking = false;
      }
    };

    const initialize = async () => {
      if (disposed || initialized || initializing) return;
      if (initializationRetry !== null) {
        window.clearTimeout(initializationRetry);
        initializationRetry = null;
      }
      initializing = true;
      const serverIndexHtml = await requestServerIndex();
      initializing = false;
      if (disposed) return;

      const serverBuildId = serverIndexHtml
        ? buildFingerprint(serverIndexHtml)
        : null;
      if (!serverBuildId) {
        setBootstrapError('Не удалось подтвердить актуальную версию. Проверка будет повторена автоматически.');
        initializationRetry = window.setTimeout(
          () => {
            initializationRetry = null;
            void initialize();
          },
          BUILD_CHECK_RETRY_MS,
        );
        return;
      }
      setBootstrapError('');

      const resolution = resolveWebBuildTarget(
        currentBuildId,
        serverIndexHtml,
        sharedBuildAnnouncement(),
      );
      if (resolution.source === 'server' && resolution.targetBuildId) {
        publishServerTarget(resolution.targetBuildId);
      }

      // App и SipPhoneProvider ещё не смонтированы: авторитетный target уже
      // согласован, поэтому устаревшая вкладка не успеет зарегистрировать SIP.
      sipManager.setWebBuildId(currentBuildId);
      initialized = true;
      await reconcileTarget(resolution.targetBuildId);
      if (disposed) return;
      setReady(true);
      timer = window.setInterval(() => { void check(); }, BUILD_CHECK_INTERVAL_MS);
    };

    const checkVisiblePage = () => {
      if (document.visibilityState !== 'visible') return;
      if (initialized) void check();
      else void initialize();
    };
    const checkOnline = () => {
      if (initialized) void check();
      else void initialize();
    };
    const receiveBuildTarget = (event: StorageEvent) => {
      if (event.key !== WEB_BUILD_UPDATE_STORAGE_KEY || !initialized) return;
      const targetBuildId = announcedBuildTarget(event.newValue);
      if (targetBuildId) void reconcileTarget(targetBuildId);
    };

    window.addEventListener('focus', checkVisiblePage);
    window.addEventListener('online', checkOnline);
    window.addEventListener('storage', receiveBuildTarget);
    window.addEventListener('pageshow', checkOnline);
    document.addEventListener('visibilitychange', checkVisiblePage);
    void initialize();

    return () => {
      disposed = true;
      if (timer !== null) window.clearInterval(timer);
      if (initializationRetry !== null) window.clearTimeout(initializationRetry);
      window.removeEventListener('focus', checkVisiblePage);
      window.removeEventListener('online', checkOnline);
      window.removeEventListener('storage', receiveBuildTarget);
      window.removeEventListener('pageshow', checkOnline);
      document.removeEventListener('visibilitychange', checkVisiblePage);
    };
  }, [controller, currentBuildId]);

  const waitingForCall = update.phase === 'WAITING_FOR_CALL';

  return <>
    {!ready && <div className="web-build-bootstrap" role="status" aria-live="polite">
      <RefreshCw className={bootstrapError ? '' : 'spinning'} />
      <span><b>{bootstrapError ? 'Требуется проверка версии' : 'Проверяем версию портала'}</b><small>{bootstrapError || 'Телефония запустится после безопасной проверки.'}</small></span>
      {bootstrapError && <button type="button" className="button primary" onClick={() => window.location.reload()}><RefreshCw /> Обновить</button>}
    </div>}
    {ready && children}
    {update.phase !== 'CURRENT' && <aside
      className={`web-build-update ${waitingForCall ? 'waiting' : 'ready'}`}
      role={update.phase === 'READY_TO_RELOAD' ? 'alert' : 'status'}
      aria-live="polite"
    >
      <span className="web-build-update-icon">
        {waitingForCall ? <Clock3 /> : <ShieldCheck />}
      </span>
      <span className="web-build-update-copy">
        <b>{waitingForCall
          ? 'Новая версия готова'
          : 'Портал обновляется автоматически'}</b>
        <small>{waitingForCall
          ? 'Разговор не прервётся. Портал обновится автоматически после его завершения.'
          : 'Телефон остаётся доступным. Если браузер остановил обновление, используйте кнопку.'}</small>
      </span>
      {update.phase === 'READY_TO_RELOAD' && <button
        type="button"
        className="button primary"
        onClick={() => { void controller.applyUpdate(); }}
      >
        <RefreshCw /> Обновить сейчас
      </button>}
    </aside>}
  </>;
}
